import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { DataNode } from 'components/WorkspaceEditor/types/types'
import { TextContentElementType } from 'models/setProjectContentItemElementsInput'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT } from 'pages/ProjectsPage/WorkspacePage/components/constants'
// ====================================================================
// CONTENT VALIDATION UTILITIES
// ====================================================================

/**
 * Checks if a node is not focusable based on its content or type.
 * Not focusable nodes include those with empty text after a colon or of type OptionGroup or UnitsOfMeasure.
 *
 * @param node - The node to check
 * @returns True if the node is not focusable, false otherwise
 */
export const isNotFocusableNode = (
    node: Partial<TextContentElement>,
    checkKeyedContent: boolean = true
): boolean | undefined => {
    return (
        (node.text?.includes(':') &&
            !node.text?.split(':')[1]?.trim() &&
            checkKeyedContent) ||
        node.type === TextContentElementType.OptionGroup ||
        node.type === TextContentElementType.UnitsOfMeasure ||
        node.type === TextContentElementType.Hyperlink
    )
}

/**
 * Checks if a content type is either Hyperlink or OptionGroup
 * These types typically require special handling during navigation
 *
 * @param type - The TextContentElementType to check
 * @returns True if the type is Hyperlink or OptionGroup
 *
 * @example
 * ```typescript
 * if (isHyperlinkOrOptionGroup(element.type)) {
 *   // Handle special content type
 * }
 * ```
 */
export const isHyperlinkOrOptionGroup = (type: string): boolean =>
    type === TextContentElementType.Hyperlink ||
    type === TextContentElementType.OptionGroup

/**
 * Validates if a text content element contains meaningful, focusable text
 * Filters out empty, whitespace-only, or non-text content
 *
 * @param node - The content element to validate
 * @returns True if the node contains valid, focusable text content
 *
 * @example
 * ```typescript
 * const focusableNodes = contentNodes.filter(isValidTextContent)
 * ```
 */
export const isValidTextContent = (
    node: Partial<TextContentElement>
): boolean =>
    node.type === TextContentElementType.Text &&
    typeof node.text === 'string' &&
    node.text.trim() !== ''

/**
 * Identifies content nodes that represent structural labels followed by a period
 * This pattern (OptionGroup/Hyperlink + ".") typically indicates non-focusable
 * section headers or labels that should be skipped during keyboard navigation
 *
 * @param contentNodes - Array of content elements to analyze
 * @returns True if the pattern matches: [OptionGroup/Hyperlink, "."]
 *
 * @example
 * ```typescript
 * // Example content: ["Requirements", "."] or ["Section 1", "."]
 * if (isStructuralContentNodeWithPeriod(contentNodes)) {
 *   // Skip this non-focusable section header during navigation
 * }
 * ```
 */
export const isStructuralContentNodeWithPeriod = (
    contentNodes: Partial<TextContentElement>[]
): boolean =>
    contentNodes.length === 2 &&
    contentNodes[1].text?.trim() === '.' &&
    contentNodes[0].type !== TextContentElementType.Text &&
    isHyperlinkOrOptionGroup(contentNodes[0].type || '')

/**
 * Identifies single content nodes that are OptionGroup or Hyperlink types
 * These are typically non-interactive elements that should be handled specially
 *
 * @param contentNodes - Array of content elements to analyze
 * @returns True if there's exactly one node of type OptionGroup or Hyperlink
 *
 * @example
 * ```typescript
 * if (isSingleHyperlinkOrOptionGroup(contentNodes)) {
 *   // Apply special navigation logic for standalone option groups
 * }
 * ```
 */
export const isSingleHyperlinkOrOptionGroup = (
    contentNodes: Partial<TextContentElement>[]
): boolean =>
    contentNodes.length === 1 &&
    contentNodes[0].type !== undefined &&
    isHyperlinkOrOptionGroup(contentNodes[0].type)

/**
 * Finds the first focusable content element from an array of content nodes
 * Applies validation rules to filter out non-interactive content patterns:
 * - Skips structural labels with periods (["Requirements", "."])
 * - Skips single non-interactive elements (standalone OptionGroups/Hyperlinks)
 * - Returns first valid text content or interactive element
 *
 * @param contentNodes - Array of content elements to search
 * @returns First focusable element or null if none found
 *
 * @example
 * ```typescript
 * const target = findFirstFocusableContent(node.contentItemNodes)
 * if (target) {
 *   focusElement(target.id)
 * }
 * ```
 */
export const findFirstFocusableContent = (
    contentNodes: Partial<TextContentElement>[]
): Partial<TextContentElement> | null => {
    if (!contentNodes?.length) return null

    // Skip structural labels with period: ["Label", "."]
    if (isStructuralContentNodeWithPeriod(contentNodes)) return null

    // Skip single non-interactive elements
    if (
        isSingleHyperlinkOrOptionGroup(contentNodes) &&
        !isValidTextContent(contentNodes[0])
    ) {
        return null
    }

    // Return first valid text content or non-special type
    return (
        contentNodes.find(
            (node) =>
                isValidTextContent(node) ||
                (node.type && !isHyperlinkOrOptionGroup(node.type))
        ) || null
    )
}

// ====================================================================
// NODE TYPE ANALYSIS UTILITIES
// ====================================================================

/**
 * Analyzes node content types for UOM/OptionGroup detection
 * Extracted from useNodeNavigation to eliminate duplication
 *
 * @param node - The node to analyze
 * @param NODE_TYPES - Node type constants
 * @returns Object with content type analysis results
 *
 * @example
 * ```typescript
 * const NODE_TYPES = { UnitsOfMeasure: 'UnitsOfMeasure', OptionGroup: 'OptionGroup' }
 * const analysis = analyzeNodeContentTypes(node, NODE_TYPES)
 *
 * if (analysis.isUOMOrOptionGroup && !analysis.hasFocusableText) {
 *   // Skip this node during navigation
 *   const nextArticle = findNextArticleNode(visibleNodes, currentIndex, 'next', NODE_TYPES)
 * }
 * ```
 */
export const analyzeNodeContentTypes = (
    node: DataNode,
    NODE_TYPES: typeof PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT
) => {
    const contentTypes = node.contentItemNodes?.map((node) => node.type) || []

    const isUOMOrOptionGroup =
        node.type === 'UnitsOfMeasure' ||
        node.type === 'OptionGroup' ||
        contentTypes.includes(NODE_TYPES.UnitsOfMeasure) ||
        contentTypes.includes(NODE_TYPES.OptionGroup) ||
        contentTypes.includes('UnitsOfMeasure') ||
        contentTypes.includes('OptionGroup')

    const hasFocusableText =
        contentTypes.includes('Text') ||
        node.contentItemNodes?.some(
            (childNode) => childNode.text && childNode.text.trim().length > 0
        )

    return {
        contentTypes,
        isUOMOrOptionGroup,
        hasFocusableText,
    }
}

/**
 * Finds the next Article node in a specific direction
 * Used when skipping UOM/OptionGroup nodes without focusable text
 *
 * @param visibleNodes - Array of visible nodes to search
 * @param currentIndex - Current position in the array
 * @param direction - Search direction ('previous' or 'next')
 * @param NODE_TYPES - Node type constants
 * @returns Found Article node or original target node
 */
export const findNextArticleNode = (
    visibleNodes: DataNode[],
    currentIndex: number,
    direction: 'previous' | 'next',
    NODE_TYPES: typeof import('pages/ProjectsPage/WorkspacePage/components/constants').PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT
): DataNode | null => {
    const startIndex =
        direction === 'previous' ? currentIndex - 1 : currentIndex + 1
    const endIndex = direction === 'previous' ? 0 : visibleNodes.length
    const step = direction === 'previous' ? -1 : 1

    for (
        let i = startIndex;
        direction === 'previous' ? i >= endIndex : i < endIndex;
        i += step
    ) {
        const node = visibleNodes[i]
        if (node?.type === NODE_TYPES.Article) {
            return node
        }
    }

    return null
}

// ====================================================================
// DOM SELECTOR UTILITIES
// ====================================================================

/**
 * Creates a CSS selector for switcher icons in the content tree
 * Used by both useArticleExpansionFocus and useSwitcherNavigation
 *
 * @param nodeKey - The node key to target
 * @returns Complete CSS selector string
 */
export const getSwitcherIconSelector = (nodeKey: string): string => {
    return `[data-testid="tree-layout"] [data-testid="ant-tree"] [data-key="${nodeKey}"] [data-testid="switcher-icon"]`
}
